import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Linking } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../../../src/api/client';
import Card from '../../../../../src/components/Card';
import Button from '../../../../../src/components/Button';
import ContentContainer from '../../../../../src/components/ContentContainer';
import { useToast } from '../../../../../src/components/Toast';
import { useTheme } from '../../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../../src/utils/theme';

interface PaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
}

interface InvoiceSummary {
  totalAmount: number;
  invoiceNumber: string;
  companyName: string;
  businessName: string;
}

export default function UserFleetInvoicePayScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [pms, setPms] = useState<PaymentMethod[]>([]);
  const [selectedPm, setSelectedPm] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<InvoiceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [inv, methods] = await Promise.all([
        apiGet<InvoiceSummary>(`/api/fleet/me/invoices/${id}`),
        apiGet<PaymentMethod[]>('/api/fleet/me/payment-methods'),
      ]);
      setInvoice(inv);
      setPms(methods);
      const def = methods.find(m => m.isDefault) ?? methods[0];
      if (def) setSelectedPm(def.id);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const doPay = async () => {
    if (!id || !selectedPm) return;
    setPaying(true);
    try {
      await apiPost(`/api/fleet/me/invoices/${id}/pay`, { paymentMethodId: selectedPm });
      toast.show({ type: 'success', title: 'Payment received', message: 'Invoice paid successfully.' });
      router.replace(`/user/fleet/invoices/${id}` as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Payment failed', message: err.message });
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <View style={styles.loadingContainer}><ActivityIndicator size="large" color={colors.primary} /></View>;
  if (!invoice) return null;

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
        <Card style={styles.summary}>
          <Text style={styles.label}>Paying</Text>
          <Text style={styles.invoiceNumber}>#{invoice.invoiceNumber}</Text>
          <Text style={styles.business}>{invoice.businessName}</Text>
          <Text style={styles.amount}>{fmt(invoice.totalAmount)}</Text>
        </Card>

        <Text style={styles.sectionTitle}>Choose Payment Method</Text>

        {pms.length === 0 ? (
          <Card style={styles.empty}>
            <Ionicons name="card-outline" size={40} color={colors.textLight} />
            <Text style={styles.emptyTitle}>No saved cards</Text>
            <Text style={styles.emptyText}>Add a card to your account from Payment Methods, then come back to pay this invoice.</Text>
            <Button
              title="Add a Card"
              onPress={() => router.push('/payment-methods' as never)}
              variant="outline"
              size="sm"
              style={{ marginTop: spacing.md }}
            />
          </Card>
        ) : (
          pms.map(pm => {
            const selected = pm.id === selectedPm;
            return (
              <TouchableOpacity key={pm.id} onPress={() => setSelectedPm(pm.id)}>
                <Card style={[styles.pmCard, selected && styles.pmCardSelected]}>
                  <View style={styles.pmRow}>
                    <Ionicons name={selected ? 'radio-button-on' : 'radio-button-off'} size={20} color={selected ? colors.primary : colors.textLight} />
                    <View style={{ flex: 1, marginLeft: spacing.md }}>
                      <Text style={styles.pmLabel}>{(pm.brand ?? 'Card').toUpperCase()} •••• {pm.last4 ?? '••••'}</Text>
                      <Text style={styles.pmSub}>
                        Expires {pm.expMonth?.toString().padStart(2, '0') ?? '--'}/{(pm.expYear ?? 0) % 100}
                        {pm.isDefault ? ' · Default' : ''}
                      </Text>
                    </View>
                  </View>
                </Card>
              </TouchableOpacity>
            );
          })
        )}

        {pms.length > 0 ? (
          <View style={{ marginTop: spacing.lg }}>
            <Button
              title={paying ? 'Processing...' : `Pay ${fmt(invoice.totalAmount)}`}
              onPress={doPay}
              loading={paying}
              disabled={!selectedPm || paying}
            />
            <Text style={styles.footnote}>
              Secure payment processed by Stripe. Charge goes to {invoice.businessName} less Rinsepay&apos;s 1% platform fee.
            </Text>
          </View>
        ) : null}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  summary: { alignItems: 'center', paddingVertical: spacing.lg, marginBottom: spacing.lg },
  label: { fontSize: fontSize.xs, color: c.textSecondary, textTransform: 'uppercase', letterSpacing: 1 },
  invoiceNumber: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.xs },
  business: { fontSize: fontSize.sm, color: c.textSecondary },
  amount: { fontSize: fontSize.hero, fontWeight: fontWeight.bold, color: c.primary, marginTop: spacing.md },

  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },

  empty: { alignItems: 'center', paddingVertical: spacing.xl },
  emptyTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.sm },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: 280 },

  pmCard: { marginBottom: spacing.sm },
  pmCardSelected: { borderColor: c.primary, borderWidth: 2 },
  pmRow: { flexDirection: 'row', alignItems: 'center' },
  pmLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  pmSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },

  footnote: { fontSize: fontSize.xs, color: c.textSecondary, textAlign: 'center', marginTop: spacing.md, maxWidth: 320, alignSelf: 'center' },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
